import { useState, useRef } from "react";
import { api } from "../lib/api";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 5 * 1024 * 1024;

export default function ImageUpload({
  value, onChange, label = "Upload image", testid = "image-upload",
  accept = "image/*", allowedTypes = null, large = false,
}) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (allowedTypes && !allowedTypes.includes(file.type)) {
      toast.error("Only JPG, PNG or WebP images are allowed.");
      if (ref.current) ref.current.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image is too large — max 5MB.");
      if (ref.current) ref.current.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Uploaded.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed.");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  }

  const thumb = large ? "w-28 h-28 rounded-2xl" : "w-14 h-14 rounded-xl";

  return (
    <div data-testid={testid} className="flex items-center gap-3">
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value} alt="" data-testid={`${testid}-preview`} className={`${thumb} object-cover border border-[#EAE4D3]`} />
          <button type="button" onClick={() => onChange("")} data-testid={`${testid}-clear`}
            className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline">
            <X className="w-3 h-3" /> Remove
          </button>
        </div>
      ) : null}
      <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
        data-testid={`${testid}-btn`}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium hover:opacity-90 disabled:opacity-60"
        style={{ background: "var(--accent-strong)", color: "#000" }}>
        {uploading ? "Uploading…" : value ? "Replace" : (<><Upload className="w-3.5 h-3.5" /> {label}</>)}
      </button>
      <input ref={ref} type="file" accept={accept} onChange={handleFile} className="hidden" data-testid={`${testid}-input`} />
    </div>
  );
}
